import { supabase } from './supabase';

export interface ChatMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  created_at: string;
  read_at?: string | null;
  image_url?: string;
  sender_name?: string;
}

class SupabaseChatService {
  /**
   * Fetch all direct messages between two users ordered chronologically.
   */
  async getMessages(userId1: string, userId2: string): Promise<ChatMessage[]> {
    const { data, error } = await supabase
      .from('messages')
      .select(
        `id, sender_id, receiver_id, content, created_at, read_at, image_url, users!messages_sender_id_fkey(full_name)`
      )
      .or(
        `and(sender_id.eq.${userId1},receiver_id.eq.${userId2}),and(sender_id.eq.${userId2},receiver_id.eq.${userId1})`
      )
      .is('hidden_at', null)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('⚠️  Error fetching messages:', error);
      return [];
    }

    return (
      data ?? []
    ).map((msg: any) => ({
      id: msg.id,
      sender_id: msg.sender_id,
      receiver_id: msg.receiver_id,
      content: msg.content,
      created_at: msg.created_at,
      read_at: msg.read_at,
      image_url: msg.image_url,
      sender_name: (msg.users as any)?.full_name,
    }));
  }

  /**
   * Send a message. Optionally include an image File that will be uploaded to the
   * `post-images` bucket before inserting the message row.
   */
  async sendMessage(
    senderId: string,
    receiverId: string,
    content: string,
    imageFile?: File
  ): Promise<boolean> {
    let imageUrl: string | null = null;

    try {
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const fileName = `chat-images/${Date.now()}_${Math.random()
          .toString(36)
          .substring(7)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('post-images')
          .upload(fileName, imageFile, {
            contentType: imageFile.type || 'image/*',
            cacheControl: '3600',
            upsert: true,
          });

        if (uploadError) {
          throw uploadError;
        }

        const { data: urlData } = supabase.storage
          .from('post-images')
          .getPublicUrl(fileName);
        imageUrl = urlData.publicUrl;
      }

      const { error } = await supabase.from('messages').insert({
        sender_id: senderId,
        receiver_id: receiverId,
        content,
        image_url: imageUrl,
      });

      if (error) throw error;

      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ Error sending message:', err);
      return false;
    }
  }

  /** Get unread count where user is receiver and read_at IS NULL */
  async getUnreadMessageCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', userId)
      .is('hidden_at', null)
      .is('deleted_at', null)
      .is('read_at', null);

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('⚠️  Error fetching unread message count:', error);
      return 0;
    }
    return count ?? 0;
  }

  async getUnreadMessageCountFromSender(
    receiverId: string,
    senderId: string
  ): Promise<number> {
    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', receiverId)
      .eq('sender_id', senderId)
      .is('hidden_at', null)
      .is('deleted_at', null)
      .is('read_at', null);

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('⚠️  Error fetching unread messages from sender:', error);
      return 0;
    }
    return count ?? 0;
  }

  async markMessagesAsRead(receiverId: string, senderId: string): Promise<void> {
    await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('receiver_id', receiverId)
      .eq('sender_id', senderId)
      .is('hidden_at', null)
      .is('deleted_at', null)
      .is('read_at', null);
  }

  /**
   * Subscribe to incoming INSERTs on the messages table for a user (sender OR receiver).
   */
  subscribeToMessages(userId: string, onMessage: (msg: ChatMessage) => void) {
    const channel = supabase
      .channel(`messages-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const m = payload.new as ChatMessage;
          if (m.sender_id === userId || m.receiver_id === userId) {
            onMessage(m);
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }
}

export const supabaseChatService = new SupabaseChatService();
